import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Display } from '@/types/display';
import { DisplayWithOwner } from '@/data/mockDisplaysData';

// Helper function to transform the database response to match our Display type
const transformDatabaseResponse = (data: any): Display => {
  // Parse the schedule if it's a string, or pass through if it's already an object
  let scheduleObject;
  if (typeof data.schedule === 'string') {
    try {
      scheduleObject = JSON.parse(data.schedule);
    } catch (e) {
      console.warn('Failed to parse schedule JSON for display:', data.id, e);
      scheduleObject = null;
    }
  } else {
    scheduleObject = data.schedule;
  }
  
  // Handle both enhanced and legacy schedule formats
  let finalSchedule = null;
  
  if (scheduleObject) {
    try {
      if (scheduleObject.timezone && scheduleObject.regular_hours) {
        // Enhanced schedule format - pass through
        finalSchedule = scheduleObject;
      } else if (scheduleObject.start_date && scheduleObject.end_date && scheduleObject.days && scheduleObject.hours) {
        // Legacy schedule format - pass through
        finalSchedule = scheduleObject;
      } else if (scheduleObject.start_date || scheduleObject.hours) {
        // Partial legacy schedule - fill in missing parts
        finalSchedule = {
          start_date: scheduleObject.start_date || '',
          end_date: scheduleObject.end_date || '',
          days: scheduleObject.days || [],
          hours: {
            start: scheduleObject.hours?.start || '',
            end: scheduleObject.hours?.end || ''
          }
        };
      }
    } catch (error) {
      console.warn('Error processing schedule object for display:', data.id, error);
      finalSchedule = null;
    }
  }
  
  return {
    ...data,
    schedule: finalSchedule,
    // Ensure the new fields exist (even if null)
    parking_other: data.parking_other || null,
    radio_station: data.radio_station || null,
    facebook: data.facebook || null,
    website: data.website || null,
    city: data.city || null,
    state: data.state || null
  };
};

export const useDisplays = () => {
  return useQuery({
    queryKey: ['displays'],
    queryFn: async (): Promise<Display[]> => {
      // Query to get active displays with images and valid coordinates
      const { data, error } = await supabase
        .from('displays')
        .select('*')
        .eq('status', 'active')
        .not('image_url', 'is', null)
        .neq('image_url', '')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .neq('latitude', 0)
        .neq('longitude', 0);
      
      if (error) {
        console.error('Error fetching displays:', error);
        throw new Error(error.message);
      }
      
      // Transform each database record to match our Display type
      return (data || []).map(transformDatabaseResponse);
    }
  });
};

export const useDisplay = (id: number | undefined) => {
  return useQuery({
    queryKey: ['display', id],
    queryFn: async (): Promise<Display | null> => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('displays')
        .select(`
          *,
          user_profile:user_profiles(
            user_id,
            display_name,
            full_name,
            avatar_url
          )
        `)
        .eq('id', id)
        .maybeSingle();
      
      if (error) {
        console.error(`Error fetching display ${id}:`, error);
        throw new Error(error.message);
      }
      
      // Transform the data to include owner information
      if (data) {
        const transformedData = transformDatabaseResponse(data);
        
        // Add real owner data if available
        if (data.user_profile) {
          transformedData.owner = {
            id: data.user_profile.user_id,
            name: data.user_profile.display_name || data.user_profile.full_name || 'Unknown Owner',
            avatar: data.user_profile.avatar_url
          };
        }
        
        return transformedData;
      }
      
      return null;
    },
    enabled: !!id
  });
};

// Helper function to convert Display to DisplayWithOwner for components that need it
export const convertToDisplayWithOwner = (display: Display | null): DisplayWithOwner | null => {
  if (!display) return null;
  
  return {
    ...display,
    isFavorite: false,
    songCount: Math.floor(Math.random() * 15) + 5, // For now, generate a random number of songs
    owner: display.owner // Only include owner if it exists, otherwise undefined
  };
};

export const useFeaturedDisplays = () => {
  return useQuery({
    queryKey: ['featured-displays'],
    queryFn: async (): Promise<Display[]> => {
      // Query to get featured displays with images
      const { data, error } = await supabase
        .from('displays')
        .select('*')
        .eq('status', 'active')
        .eq('featured', true)
        .not('image_url', 'is', null)
        .neq('image_url', '');
      
      if (error) {
        console.error('Error fetching featured displays:', error);
        throw new Error(error.message);
      }
      
      // Transform each database record to match our Display type
      return (data || []).map(transformDatabaseResponse);
    }
  });
};
